import { useState, useRef, useEffect } from "react";

/**
 * Tracks which field is focused inside a container by delegating
 * focusin/focusout events and reading `data-hint-field` attributes.
 *
 * Returns `{ focusedField, containerRef }` where `focusedField` is the
 * string value of the closest `data-hint-field` attribute (or null).
 *
 * An 80ms debounce on blur prevents flicker when focus moves between
 * sibling elements within the same field (e.g. tiptap toolbar → editor).
 */
export function useFieldFocus() {
  const [focusedField, setFocusedField] = useState(null);
  const containerRef = useRef(null);
  const blurTimeoutRef = useRef(null);
  const mouseDownInsideRef = useRef(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleMouseDown = (e) => {
      // Track if the mousedown happened inside the container but outside
      // a hint field (e.g. clicking the hint card to copy content).
      // Non-interactive elements don't receive focus, so activeElement
      // falls back to <body> and the focusout handler would clear the hint.
      if (!e.target.closest("[data-hint-field]")) {
        mouseDownInsideRef.current = true;
      }
    };

    const handleMouseUp = () => {
      mouseDownInsideRef.current = false;
    };

    const handleFocusIn = (e) => {
      clearTimeout(blurTimeoutRef.current);
      const fieldEl = e.target.closest("[data-hint-field]");
      setFocusedField(fieldEl ? fieldEl.dataset.hintField : null);
    };

    const handleFocusOut = () => {
      blurTimeoutRef.current = setTimeout(() => {
        // If the user is clicking inside the container (e.g. on the hint
        // card), keep the current hint visible.
        if (mouseDownInsideRef.current) return;

        if (!container.contains(document.activeElement)) {
          setFocusedField(null);
        } else {
          const fieldEl = document.activeElement.closest(
            "[data-hint-field]",
          );
          if (fieldEl) {
            setFocusedField(fieldEl.dataset.hintField);
          }
        }
      }, 80);
    };

    container.addEventListener("mousedown", handleMouseDown);
    container.addEventListener("mouseup", handleMouseUp);
    container.addEventListener("focusin", handleFocusIn);
    container.addEventListener("focusout", handleFocusOut);

    return () => {
      clearTimeout(blurTimeoutRef.current);
      container.removeEventListener("mousedown", handleMouseDown);
      container.removeEventListener("mouseup", handleMouseUp);
      container.removeEventListener("focusin", handleFocusIn);
      container.removeEventListener("focusout", handleFocusOut);
    };
  }, []);

  return { focusedField, containerRef };
}
